import { Controller } from '@hotwired/stimulus'

// Checks server-side session status before showing timeout warning.
// Prevents auto-logout when another tab has kept the session alive.
export default class extends Controller {
  static targets = ['modal']

  static values = {
    expirePath: String,
    timeoutSeconds: Number,
    warningSeconds: { type: Number, default: 90 },
  }

  connect() {
    this.timerId = null
    this.backdrop = null
    this.scheduleCheck()
  }

  disconnect() {
    this.clearTimer()
    this.hideWarning()
  }

  scheduleCheck() {
    this.clearTimer()
    const delaySec = Math.max(
      this.timeoutSecondsValue - this.warningSecondsValue,
      10,
    )
    this.timerId = setTimeout(() => this.checkSession(), delaySec * 1000)
  }

  async checkSession() {
    let response
    try {
      response = await fetch('/session/status', {
        headers: { Accept: 'application/json' },
      })
    } catch {
      this.timerId = setTimeout(() => this.checkSession(), 60_000)
      return
    }

    if (response.status === 401) {
      this.forceLogout()
      return
    }

    const { seconds_remaining } = await response.json()

    if (seconds_remaining <= 0) {
      this.forceLogout()
    } else if (seconds_remaining <= this.warningSecondsValue) {
      this.showWarning(seconds_remaining)
    } else {
      this.hideWarning()
      const nextCheckSec = Math.max(
        seconds_remaining - this.warningSecondsValue,
        10,
      )
      this.timerId = setTimeout(() => this.checkSession(), nextCheckSec * 1000)
    }
  }

  async staySignedIn(event) {
    event.preventDefault()

    try {
      const csrfToken = document.querySelector(
        "meta[name='csrf-token']",
      )?.content
      await fetch('/session/heartbeat', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken, Accept: 'application/json' },
      })
    } catch {
      // ignore network errors on heartbeat
    }

    this.hideWarning()
    this.scheduleCheck()
  }

  get isDialog() {
    return this.hasModalTarget && this.modalTarget.tagName === 'DIALOG'
  }

  showWarning(secondsRemaining) {
    if (!this.hasModalTarget) return

    this.clearTimer()

    if (this.isDialog) {
      this.modalTarget.showModal()
    } else {
      this.modalTarget.style.display = 'block'
      this.modalTarget.classList.add('in', 'show')
      document.body.classList.add('modal-open')

      if (!this.backdrop) {
        this.backdrop = document.createElement('div')
        this.backdrop.className = 'modal-backdrop fade in show'
        this.backdrop.style.opacity = '0.85'
        document.body.appendChild(this.backdrop)
      }
    }

    this.timerId = setTimeout(
      () => this.checkSession(),
      secondsRemaining * 1000,
    )
  }

  hideWarning() {
    if (!this.hasModalTarget) return

    if (this.isDialog) {
      if (this.modalTarget.open) this.modalTarget.close()
    } else {
      this.modalTarget.style.display = 'none'
      this.modalTarget.classList.remove('in', 'show')
      document.body.classList.remove('modal-open')

      if (this.backdrop) {
        this.backdrop.remove()
        this.backdrop = null
      }
    }
  }

  forceLogout() {
    this.clearTimer()
    this.hideWarning()
    window.location.href = this.expirePathValue
  }

  clearTimer() {
    if (this.timerId) {
      clearTimeout(this.timerId)
      this.timerId = null
    }
  }
}
